import SlimSelect from '../../../lib/slim-select-custom';
import SlimSelectHelper, { SelectOption } from '../../helpers/slim-select-helper';

const BRANDS_ENDPOINT = () => `${window.ACC.config.encodedContextPath}/vehiclepool/brands`;
const MODELS_ENDPOINT = (brandId: string) => `${window.ACC.config.encodedContextPath}/vehiclepool/models/${brandId}`;
const TYPES_ENDPOINT = (modelId: string) => `${window.ACC.config.encodedContextPath}/vehiclepool/modeltypes/${modelId}`;

type BrandResponse = {
  code: string,
  name: string
}[];

type ModelResponse = {
  id: string,
  code: string,
  name: string,
  brandCode: string,
  seriesId: string
}[];

type TypeResponse = {
  id: string,
  code: string,
  name: string,
  brandCode: string,
  year: string,
  description: string,
  doors: string | null,
  modelId: string,
  bodyTypeId: string,
  engineId: string,
  transmissionId: string
}[];

type YearRange = { min: number, max: number };

export default class VehicleSelector {
  private readonly vinInput: HTMLInputElement;
  private readonly codeInput: HTMLInputElement;
  private readonly brandSelect: SlimSelect;
  private readonly modelSelect: SlimSelect;
  private readonly yearSelect: SlimSelect;
  private readonly typeSelect: SlimSelect;

  private readonly noResultsMessage: HTMLParagraphElement;
  private readonly validationMessage: HTMLParagraphElement;

  private types: TypeResponse | null = null; // Options for yearSelect are generated from this response

  constructor(private container: HTMLElement) {
    this.vinInput = container.querySelector('.js-vin') as HTMLInputElement;
    this.codeInput = container.querySelector('.js-code') as HTMLInputElement;

    this.brandSelect = new SlimSelect({
      select: container.querySelector('.js-brands') as Element,
      allowDeselect: true,
      allowDeselectOption: true,
    });

    this.modelSelect = new SlimSelect({
      select: container.querySelector('.js-models') as Element,
      allowDeselect: true,
      allowDeselectOption: true,
    });

    this.yearSelect = new SlimSelect({
      select: container.querySelector('.js-years') as Element,
      allowDeselect: true,
      allowDeselectOption: true,
    });

    this.typeSelect = new SlimSelect({
      select: container.querySelector('.js-types') as Element,
      allowDeselect: true,
      allowDeselectOption: true,
    });

    this.noResultsMessage = container.querySelector('.js-noResultMessage') as HTMLParagraphElement;
    this.validationMessage = container.querySelector('.js-validationMessage') as HTMLParagraphElement;
    this.bindResetFormSections();
    this.bindModelsToBrand();
    this.bindYearsToModel();
    this.bindTypesToYear();
    this.refreshBrands();
    this.bindValidationMessage();
  }

  private bindValidationMessage(): void {
    const form = this.container.closest('form');
    if (form) {
      form.addEventListener('submit', (event) => {
        if (this.getBrand() && !this.getType()) {
          event.preventDefault();
          event.stopPropagation();
          event.stopImmediatePropagation();
          this.toggleValidationMessage(true);
        } else {
          this.toggleValidationMessage(false);
        }
      });
    }

    Array.from(this.container.querySelectorAll('select'))
      .forEach((select) => select.addEventListener('change', () => {
        this.toggleValidationMessage(false);
      }));
  }

  private bindResetFormSections() {
    // Only one section of this form should be filled, so if the user partially fills one and touches another
    // we reset the other sections so it is obvious which data is being submitted
    let suppressVinChange = false;
    let suppressCodeChange = false;

    this.vinInput.addEventListener('input', () => {
      suppressVinChange = true;
      if (this.getBrand()) {
        this.setBrand(''); // This will reset the other select boxes
      }
      this.codeInput.value = '';
      $(this.codeInput).parsley().validate();
      suppressVinChange = false;
    });

    this.codeInput.addEventListener('input', () => {
      suppressCodeChange = true;
      if (this.getBrand()) {
        this.setBrand(''); // This will reset the other select boxes
      }
      this.vinInput.value = '';
      $(this.vinInput).parsley().validate();
      suppressCodeChange = false;
    });

    this.typeSelect.select.element.addEventListener('change', () => {
      if (!suppressVinChange) {
        this.vinInput.value = '';
        $(this.vinInput).parsley().validate();
      }
      if (!suppressCodeChange) {
        this.codeInput.value = '';
        $(this.codeInput).parsley().validate();
      }
    });
  }

  private bindModelsToBrand(): void {
    this.brandSelect.select.element.addEventListener('change', () => {
      this.setModel('');
      this.modelSelect.disable();
      this.setYear('');
      this.yearSelect.disable();
      this.setType('');
      this.typeSelect.disable();

      if (this.getBrand()) {
        this.refreshModels();
      }
    });
  }

  private bindYearsToModel(): void {
    this.modelSelect.select.element.addEventListener('change', () => {
      this.setYear('');
      this.yearSelect.disable();
      this.setType('');
      this.typeSelect.disable();
      this.types = null;

      if (this.getModel()) {
        this.fetchTypes()
          .then((types) => {
            this.setTypes(types);
          })
          .finally(() => this.setYearOptionsFromTypes());
      }
    });
  }

  private bindTypesToYear(): void {
    this.yearSelect.select.element.addEventListener('change', () => {
      this.setType('');
      this.typeSelect.disable();

      const typeOptions = (this.types ?? [])
        .filter((type) => type.year === this.getYear())
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(({
          code,
          name,
        }) => ({
          text: name,
          value: code,
          selected: false,
        }));

      SlimSelectHelper.setOptions(this.typeSelect, typeOptions);
    });
  }

  private setTypes(types: TypeResponse) {
    this.types = types;
  }

  private toggleNoResultsMessage(show: boolean) {
    this.noResultsMessage.classList.toggle('d-none', !show);
  }

  private toggleValidationMessage(show: boolean) {
    this.validationMessage.classList.toggle('d-none', !show);
  }

  isValid(): boolean {
    return this.getType().length > 0 || this.vinInput.value.length === 17 || this.codeInput.value.length > 0;
  }

  getContainer(): HTMLElement {
    return this.container;
  }

  getBrand(): string {
    return this.brandSelect.selected() as string;
  }

  setBrand(value: string): void {
    this.brandSelect.set(value);
  }

  getModel(): string {
    return this.modelSelect.selected() as string;
  }

  setModel(value: string): void {
    this.modelSelect.set(value);
  }

  getYear(): string {
    return this.yearSelect.selected() as string;
  }

  setYear(value: string): void {
    this.yearSelect.set(value);
  }

  getType(): string {
    return this.typeSelect.selected() as string;
  }

  setType(value: string): void {
    this.typeSelect.set(value);
  }

  private refreshBrands(): void {
    fetch(BRANDS_ENDPOINT())
      .then((response) => {
        if (!response.ok) {
          throw new Error('Could not fetch brands');
        }
        return response.json();
      })
      .then((brands: BrandResponse) => brands
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(({
          code,
          name,
        }) => ({
          text: name,
          value: code,
          selected: false,
        })))
      .then((brands) => SlimSelectHelper.setOptions(this.brandSelect, brands))
      .catch(() => { /* do nothing */ });
  }

  private refreshModels(): void {
    fetch(MODELS_ENDPOINT(this.getBrand()))
      .then((response) => response.json())
      .then((models: ModelResponse) => {
        this.toggleNoResultsMessage(!models?.length);

        return models
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(({
            id,
            name,
          }) => ({
            text: name,
            value: id,
            selected: false,
          }));
      })
      .then((models) => SlimSelectHelper.setOptions(this.modelSelect, models))
      .catch(() => { /* do nothing */ });
  }

  // eslint-disable-next-line class-methods-use-this
  private fetchTypes(): Promise<TypeResponse> {
    return fetch(TYPES_ENDPOINT(this.getModel()))
      .then((response) => (response.json() as Promise<TypeResponse>));
  }

  private setYearOptionsFromTypes = () => {
    const yearOptions = this.generateYearOptions();
    this.toggleNoResultsMessage(yearOptions.length === 0);
    SlimSelectHelper.setOptions(this.yearSelect, yearOptions);
  };

  private getYearRangeFromTypes(): YearRange {
    if (this.types === null) {
      throw new Error('No types set to calculate range');
    }

    return this.types.reduce<YearRange>((prev, { year }) => {
      const yearNumeric = parseInt(year, 10);
      const min = (yearNumeric < (prev.min ?? Infinity) ? yearNumeric : prev.min);
      const max = (yearNumeric > (prev.max ?? 0) ? yearNumeric : prev.max);

      return {
        min,
        max,
      };
    }, {
      min: undefined,
      max: undefined,
    } as any);
  }

  private generateYearOptions(): SelectOption[] {
    try {
      const {
        min,
        max,
      } = this.getYearRangeFromTypes();
      const options: SelectOption[] = [];
      for (let i = min; i <= max; i++) {
        options.push({
          text: i.toString(),
          value: i.toString(),
        });
      }
      return options;
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
      return [];
    }
  }
}
